import { useMemo } from "react";

import { Button } from "../button";
import { BasicTooltip } from "../Tooltip";
import { SubscriptionStatus } from "../../../types/organization-usage";

import { Plan, PlanFeatures as PlanFeaturesType } from "./constants";
import { PlanBadges } from "./PlanBadges";
import { PlanPricing } from "./PlanPricing";
import { PlanFeatures } from "./PlanFeatures";
import { getButtonText, getPlanCtaDisabledReason } from "./utils";

interface PlanCardProps {
  plan: Plan;
  features: PlanFeaturesType;
  isLoading: boolean;
  disabled: boolean;
  hasLegacySubscription: boolean;
  isTrialing: boolean;
  isCurrentPlan: boolean;
  onButtonClick: () => void;
  subscriptionStatus: SubscriptionStatus;
  totalCurrentMembers: number;
  maxUsersForPlan: number;
  isMaxUsersReached: boolean;
  isPaymentBlocked: boolean;
  isBannerPermissionDisabled: boolean;
}

export const PlanCard = ({
  plan,
  features,
  isLoading,
  disabled,
  hasLegacySubscription,
  isTrialing,
  isCurrentPlan,
  subscriptionStatus,
  onButtonClick,
  totalCurrentMembers,
  maxUsersForPlan,
  isMaxUsersReached,
  isPaymentBlocked,
  isBannerPermissionDisabled,
}: PlanCardProps) => {
  const isFreePlan = plan.name.toLowerCase() === "free";
  const isEnterprisePlan = plan.name.toLowerCase() === "enterprise";
  const isTeamPlan = plan.name.toLowerCase() === "team";

  const hasLegacyDiscount = hasLegacySubscription && !isFreePlan && !isEnterprisePlan;
  const showTrialBadge = isTrialing && isTeamPlan;

  const buttonText = useMemo(
    () =>
      getButtonText(
        plan.name,
        subscriptionStatus?.subscription?.plan_type ?? "",
        subscriptionStatus?.subscription?.status ?? "",
        hasLegacySubscription && !subscriptionStatus,
      ),
    [plan.name, hasLegacySubscription, subscriptionStatus],
  );

  const isButtonDisabled =
    isLoading || disabled || (isCurrentPlan && !isTrialing) || (isTrialing && isFreePlan);

  const disabledTooltipReason = useMemo(
    () =>
      getPlanCtaDisabledReason({
        isButtonDisabled,
        isLoading,
        isCurrentPlan,
        isTrialing,
        isTargetFreePlan: isFreePlan,
        isMaxUsersReached,
        totalCurrentMembers,
        maxUsersForPlan,
        targetPlanLabel: plan.name,
        isPaymentBlocked,
        stripeSubscriptionStatus: subscriptionStatus?.subscription?.stripe_subscription_status,
        isBannerPermissionDisabled,
      }),
    [
      isBannerPermissionDisabled,
      isButtonDisabled,
      isCurrentPlan,
      isFreePlan,
      isLoading,
      isMaxUsersReached,
      isPaymentBlocked,
      isTrialing,
      maxUsersForPlan,
      plan.name,
      subscriptionStatus?.subscription?.stripe_subscription_status,
      totalCurrentMembers,
    ],
  );

  const ctaButton = (
    <Button
      variant={plan.popular ? "default" : "outline"}
      size="sm"
      onClick={onButtonClick}
      disabled={isButtonDisabled}
      className="min-h-[32px] w-full"
    >
      {isLoading ? "Loading..." : buttonText}
    </Button>
  );

  return (
    <div
      className={`relative flex h-fit max-h-[70vh] min-h-[365px] w-[285px] flex-col rounded-lg border p-4 ${
        plan.popular ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white"
      }`}
    >
      <PlanBadges
        isPopular={plan.popular}
        hasLegacyDiscount={hasLegacyDiscount}
        isTrialing={showTrialBadge}
        isTeamPlan={isTeamPlan}
      />

      <div className="mb-4 text-center">
        <h3 className={`text-lg font-bold ${plan.popular ? "text-blue-900" : "text-gray-900"}`}>
          {plan.name}
        </h3>

        <PlanPricing
          planName={plan.name}
          price={plan.price}
          isPopular={plan.popular}
          hasLegacyDiscount={hasLegacyDiscount}
        />

        <p className="text-xs text-gray-600">{plan.description}</p>
      </div>

      <PlanFeatures features={features} />

      {isButtonDisabled && disabledTooltipReason ? (
        <BasicTooltip content={disabledTooltipReason} side="top">
          <span className="inline-flex w-full">{ctaButton}</span>
        </BasicTooltip>
      ) : (
        ctaButton
      )}
    </div>
  );
};
